import { useTranslation } from 'react-i18next';
import AllergenIcon from './AllergenIcon';
import type { AllergenCode } from './allergens';

interface Props {
  /** `undefined` = non renseigné, `[]` = renseigné sans allergène majeur. */
  codes: AllergenCode[] | undefined;
  /** Le restaurant a déclaré « peut contenir des traces » pour ce produit. */
  traces?: boolean;
  /** Le restaurant a déclaré au moins un produit. Faux (rien déclaré, chargement,
   *  panne) ⇒ aucune section : « demandez au restaurant » sur chaque fiche d'un
   *  restaurant qui n'utilise pas le registre serait du bruit pur. */
  restaurantDeclares: boolean;
}

/** Fiche produit : jamais « sans allergène » quand le restaurant n'a rien déclaré. */
export default function AllergenSection({ codes, traces = false, restaurantDeclares }: Props) {
  const { t } = useTranslation();
  if (codes === undefined && !restaurantDeclares) return null;
  const note = 'm-0 font-mono text-xs text-ink/70';
  return (
    <section data-testid="product-allergens" className="mb-4 flex flex-col gap-1.5">
      {codes === undefined ? (
        <p className={note}>{t('menu.allergens.undeclared')}</p>
      ) : codes.length === 0 ? (
        <>
          {/* Des traces déclarées interdisent l'affirmation « sans allergène majeur ». */}
          <p className="m-0 font-mono text-xs font-bold text-ink">
            {t(traces ? 'menu.allergens.tracesOnly' : 'menu.allergens.noneMajor')}
          </p>
          <p className={note}>{t('menu.allergens.severeHint')}</p>
        </>
      ) : (
        <>
          <h3 className="m-0 font-mono text-xs font-bold uppercase text-ink">{t('menu.allergens.title')}</h3>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
            {codes.map((c) => (
              <li
                key={c}
                className="inline-flex items-center gap-1.5 rounded-full border border-solid border-ink/30 px-2 py-0.5 font-mono text-xs text-ink"
              >
                <AllergenIcon code={c} size={16} className="shrink-0" />
                {t(`menu.allergens.names.${c}`)}
              </li>
            ))}
          </ul>
          {traces && <p className={note}>{t('menu.allergens.tracesOther')}</p>}
          <p className={note}>{t('menu.allergens.severeHint')}</p>
        </>
      )}
    </section>
  );
}
